import { useEffect, useState } from "react";
import { getAllDocument } from "../API/DocuSealServices";
import Loader from "./Loader";
import {
  Calendar,
  Search,
  FileSignature,
  SendHorizontal,
  ExternalLink,
} from "lucide-react";

const LIMIT = 10;

export default function DocumentList({ nextStep }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    load();
  }, [page, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (searchInput.trim() === "") {
      setSearch("");
      setPage(1);
    }
  }, [searchInput]);

  const load = async () => {
    try {
      setLoading(true);

      const res = await getAllDocument({
        page,
        limit: LIMIT,
        search,
      });

      setItems(res.submissions || []);
      setTotalPages(res.pagination?.total_pages || 1);
      setTotalCount(res.pagination?.total_count || 0);
    } catch (err) {
      console.error(err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
  };

  const handleView = (item) => {
    if (item.status === "completed") {
      const pdfUrl = item.documents?.[0]?.url;
      if (!pdfUrl) return alert("Signed PDF not available");
      window.open(pdfUrl, "_blank");
      return;
    }

    if (!item.embed_src) return alert("Signing link not available");
    window.open(item.embed_src, "_blank");
  };

  const StartFlow = () => (
    <div className="flex justify-center">
      <div className="max-w-lg w-full text-center rounded-3xl border bg-white p-4 my-1 shadow-sm hover:shadow-lg transition">
        <div className="mx-auto mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-blue-500">
          <FileSignature className="h-5 w-5 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">Sign Documents</h2>
        <p className="mt-1 text-sm text-slate-500">
          Upload PDFs, add fields, and send for e-signing.
        </p>
        <button
          onClick={nextStep}
          className="mt-6 w-full sm:w-auto rounded-xl px-9 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold hover:from-sky-600 hover:to-blue-700 active:scale-95 transition"
        >
          Get Started
        </button>
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <FileSignature className="h-14 w-14 text-gray-300 mb-4" />
      <h3 className="text-lg font-semibold text-gray-700">
        No submissions available
      </h3>
      <p className="text-sm text-gray-500 mt-1">
        Upload a document or adjust your search.
      </p>
    </div>
  );

  return (
    <>
      <StartFlow />

      <div className="space-y-4 p-4 md:p-6">
        <hr />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold">Submissions</h1>

          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Search by reference number…"
              className="w-full pl-10 pr-12 py-2 rounded-lg border focus:ring-2 focus:ring-sky-500 outline-none"
            />
            <button
              onClick={handleSearch}
              disabled={!searchInput.trim()}
              className="group absolute right-2 top-1/2 -translate-y-1/2
                h-8 w-8 flex items-center justify-center
                rounded-md bg-sky-500 text-white
                hover:bg-sky-600 active:scale-95
                disabled:opacity-40"
            >
              <SendHorizontal
                size={16}
                className={`transition-transform duration-300 ${
                  searchInput.trim()
                    ? "group-hover:translate-x-1 group-hover:-translate-y-0.5"
                    : ""
                }`}
              />
            </button>
          </div>
        </div>

        {loading ? (
          <Loader show label="Loading..." />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {items.map((item) => {
              const expiryRaw =
                item.expire_at || item.expiry_date || item.expiry;
              const expiryDate = expiryRaw ? new Date(expiryRaw) : null;
              const isExpired =
                expiryDate && !isNaN(expiryDate) && expiryDate < new Date();

              const rawStatus = item.status?.toUpperCase();
              const isCompleted = rawStatus === "COMPLETED";
              // Override pending → EXPIRED if expiry date has passed
              const status =
                isExpired && !isCompleted ? "EXPIRED" : rawStatus;
              const docName = item.documents?.[0]?.name || "Document";

              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleView(item)}
                  onKeyDown={(e) => e.key === "Enter" && handleView(item)}
                  className="group cursor-pointer flex flex-col md:flex-row md:items-center justify-between
                  rounded-2xl border border-gray-200 bg-white p-3 gap-4
                  shadow-sm transition-all duration-300
                  hover:shadow-lg hover:-translate-y-[2px]"
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 max-w-xs">
                      <div
                        className="font-bold text-slate-900 truncate w-[300px]"
                        title={docName}
                      >
                        {docName}
                      </div>

                      <button
                        onClick={() => handleView(item)}
                        className="text-slate-300 hover:text-slate-900"
                        aria-label="Open document"
                      >
                        <ExternalLink size={16} />
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {item.customerFirstName} {item.customerLastName}
                      </span>

                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {item.customerEmail}
                      </span>

                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                        {item.customerPhone}
                      </span>

                      {expiryDate && !isNaN(expiryDate) && (
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                            isExpired
                              ? "bg-red-100 text-red-700"
                              : "bg-rose-50 text-rose-700"
                          }`}
                        >
                          <Calendar className="h-3 w-3" />
                          {isExpired ? "Expired" : "Expires"}{" "}
                          {expiryDate.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                          {" · "}
                          {expiryDate.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-start md:items-end gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-800 text-white">
                        #{item.referenceNumber}
                      </span>
                      {item.referenceType && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700">
                          {item.referenceType}
                        </span>
                      )}
                      {item.documentType && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                          {item.documentType}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 bg-sky-50 text-sky-700 px-1 py-1 rounded-md text-xs">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(item.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                      <span className="italic text-blue-600">
                        ·{" "}
                        {new Date(item.created_at).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <span
                        className={`inline-flex items-center gap-2
                        px-4 py-1.5 rounded-full
                        text-xs font-semibold
                        ${
                          isCompleted
                            ? "bg-green-100 text-green-700"
                            : isExpired
                            ? "bg-red-100 text-red-700"
                            : "bg-sky-100 text-sky-700"
                        }`}
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${
                            isCompleted
                              ? "bg-green-600"
                              : isExpired
                              ? "bg-red-600"
                              : "bg-sky-600"
                          }`}
                        />
                        {status}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-sm text-gray-600">
                  Showing {(page - 1) * LIMIT + 1}–
                  {Math.min(page * LIMIT, totalCount)} of {totalCount}
                </p>

                <div className="flex gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1 rounded border disabled:opacity-40"
                  >
                    Prev
                  </button>

                  {Array.from({ length: totalPages }).map((_, i) => {
                    const p = i + 1;
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1 rounded ${
                          page === p
                            ? "bg-sky-500 text-white"
                            : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}

                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1 rounded border disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
